if (!customElements.get('category-collection-tabs')) {
  class CategoryCollectionTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-cct-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-cct-panel]'));
      this.indicator = this.querySelector('[data-cct-indicator-thumb]');
      this.indicatorTrack = this.querySelector('[data-cct-indicator-track]');
      this.activeIndex = 0;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activate(index));
      });

      this.activate(0, false);
      this.navScroll = this.querySelector('[data-cct-nav-scroll]');
      this.navList = this.navScroll?.querySelector('.category-collection-tabs__nav');
      this.navWrap = this.querySelector('[data-cct-nav-wrap]');
      this.productsEl = this.querySelector('.category-collection-tabs__products');

      this.resizeObserver = new ResizeObserver(() => this.updateIndicator());
      if (this.indicatorTrack) {
        this.resizeObserver.observe(this.indicatorTrack);
      }
      this.tabs.forEach((tab) => this.resizeObserver.observe(tab));
      if (this.navList) {
        this.resizeObserver.observe(this.navList);
      }

      this.onIndicatorResize = () => this.updateIndicator();
      this.navScroll?.addEventListener('scroll', this.onIndicatorResize, { passive: true });
      window.addEventListener('resize', this.onIndicatorResize);
      this.setupStickyNav();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.stickyNavObserver?.disconnect();
      window.removeEventListener('scroll', this.onStickyNavScroll);
      window.removeEventListener('resize', this.onStickyNavScroll);
      window.removeEventListener('resize', this.onIndicatorResize);
      this.navScroll?.removeEventListener('scroll', this.onIndicatorResize);
    }

    setupStickyNav() {
      if (!this.navWrap) return;

      this.onStickyNavScroll = () => {
        this.updateStickyNavState();
        this.updateIndicator();
      };
      window.addEventListener('scroll', this.onStickyNavScroll, { passive: true });
      window.addEventListener('resize', this.onStickyNavScroll, { passive: true });

      if ('IntersectionObserver' in window) {
        this.stickyNavObserver = new IntersectionObserver(
          () => this.updateStickyNavState(),
          { root: null, rootMargin: '0px', threshold: [0, 1] }
        );
        this.stickyNavObserver.observe(this);
      }

      this.updateStickyNavState();
    }

    getHeaderHeight() {
      const headerHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      );
      return Number.isFinite(headerHeight) ? headerHeight : 0;
    }

    getStickyTop() {
      const header = document.querySelector('.section-header');
      const stickyHeader = document.querySelector('sticky-header');
      if (!header) return 0;

      const isAlwaysSticky =
        stickyHeader?.getAttribute('data-sticky-type') === 'always' ||
        stickyHeader?.getAttribute('data-sticky-type') === 'reduce-logo-size';

      if (isAlwaysSticky) {
        return this.getHeaderHeight();
      }

      if (header.classList.contains('shopify-section-header-hidden')) {
        return 0;
      }

      if (header.classList.contains('shopify-section-header-sticky')) {
        return this.getHeaderHeight();
      }

      const headerBottom = header.getBoundingClientRect().bottom;
      return Math.max(0, Math.round(headerBottom));
    }

    updateStickyNavState() {
      if (!this.navWrap) return;

      const offset = this.getStickyTop();
      this.navWrap.style.setProperty('--cct-sticky-top', `${offset}px`);

      const navRect = this.navWrap.getBoundingClientRect();
      const sectionRect = this.getBoundingClientRect();
      const isInSection = sectionRect.bottom > offset && sectionRect.top < window.innerHeight;
      const isStuck = isInSection && navRect.top <= offset + 1 && navRect.bottom > offset;

      this.navWrap.classList.toggle('is-stuck', isStuck);
    }

    activate(index, scrollTab = true) {
      if (index < 0 || index >= this.tabs.length) return;

      this.activeIndex = index;

      this.tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });

      if (scrollTab) {
        if (window.innerWidth < 750) {
          this.tabs[index]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
        this.scrollToCategoryStart();
      }

      this.updateIndicator();
    }

    getCategoryScrollOffset() {
      const navHeight = this.navWrap?.offsetHeight || 0;
      const stickyHeader = document.querySelector('sticky-header');
      const isAlwaysSticky =
        stickyHeader?.getAttribute('data-sticky-type') === 'always' ||
        stickyHeader?.getAttribute('data-sticky-type') === 'reduce-logo-size';

      let headerOffset;
      if (isAlwaysSticky) {
        headerOffset = this.getHeaderHeight();
      } else {
        const productsTop = window.scrollY + this.productsEl.getBoundingClientRect().top;
        const scrollingUp = productsTop < window.scrollY - 2;
        headerOffset = scrollingUp ? this.getHeaderHeight() : this.getStickyTop();
      }

      return headerOffset + navHeight;
    }

    scrollToCategoryStart() {
      if (!this.productsEl) return;

      const scrollOffset = this.getCategoryScrollOffset();
      const productsViewportTop = this.productsEl.getBoundingClientRect().top;

      if (Math.abs(productsViewportTop - scrollOffset) < 2) return;

      window.scrollTo({
        top: Math.max(0, window.scrollY + productsViewportTop - scrollOffset),
        behavior: 'smooth',
      });
    }

    updateIndicator() {
      if (!this.indicator || !this.indicatorTrack || window.innerWidth >= 750) return;

      const activeTab = this.tabs[this.activeIndex]?.closest('.category-collection-tabs__tab');
      if (!activeTab || !this.navList) return;

      const trackWidth = this.indicatorTrack.clientWidth;
      const tabItems = Array.from(
        this.navList.querySelectorAll('.category-collection-tabs__tab:not(.category-collection-tabs__tab--more)')
      );
      const lastTab = tabItems[tabItems.length - 1];
      const listWidth = lastTab ? lastTab.offsetLeft + lastTab.offsetWidth : this.navList.scrollWidth;
      if (!trackWidth || !listWidth) return;

      const tabLeft = activeTab.offsetLeft;
      const tabWidth = activeTab.offsetWidth;
      const thumbWidth = Math.max((tabWidth / listWidth) * trackWidth, 24);
      const maxLeft = Math.max(trackWidth - thumbWidth, 0);
      const thumbLeft = Math.min((tabLeft / listWidth) * trackWidth, maxLeft);

      this.indicator.style.width = `${thumbWidth}px`;
      this.indicator.style.transform = `translateX(${thumbLeft}px)`;
    }
  }

  customElements.define('category-collection-tabs', CategoryCollectionTabs);
}
